import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

import { makeStyles } from '@mui/styles';

import {
  Brand,
  CaregiverRelationship,
  Enum,
  MemberContactMethodType,
  MemberContactTimeType,
  MemberRisk,
  MemberStatus,
  PhoneType,
  ProgramExtensionStatusReason,
  ReasonWithdraw,
  VirtualVisitPlatform,
  VirtualVisitStatus,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  EmployeeGroup,
  MemberPhone,
  MemberProgramExtension,
  Patient,
  Phone,
  ProgramExtension,
  ProgramExtensionStatus,
  UserPhone,
} from '@vestahealthcare/common/models';
import { DATE_TIME_FORMAT } from '@vestahealthcare/common/utils/constants';
import { isValidPhone } from '@vestahealthcare/common/utils/format';
import { formatPhone } from '@vestahealthcare/common/utils/phone';

import {
  Button,
  CheckboxWithLabel,
  CollapsableSidebar,
  DateTimePicker,
  PanelInfo,
  PhoneInput,
  Select,
  SelectPhoneOption,
  SwitchGroup,
  TextArea,
  TextInput,
  Tooltip,
} from 'styleguide-v2';
import CheckboxGroup, {
  CheckboxItem,
} from 'styleguide-v2/src/components/CheckboxWithLabel/CheckboxGroup';

import {
  DatesTimeItem,
  EditDateTimes,
} from 'dash/src/components/EditDateTimes';
import {
  MemberContactInfoParams,
  MemberContactInfoParsedResponse,
  PatientParam,
  ProgramExtensionBulkParams,
} from 'dash/src/services/PatientServices';
import Session from 'dash/src/services/SessionServices';

import {
  buildContactInfoParams,
  buildProgramExtensionParams,
  getDataFromContactInfo,
  getOtherContactType,
} from '../../paramUtils';
import { MemberProgramEditProgramExtensions } from './MemberProgramEditProgramExtensions';

const I18N_NAMESPACE = 'personalDetails.programDetailsCard.modal';

const UNASSIGNED_GROUP = new EmployeeGroup({
  name: translate('global.unassigned'),
});

const UNASSIGNED_SELECTABLE: Selectable = {
  value: -1,
  label: translate('global.unassigned'),
};

type Props = {
  contactInfo?: MemberContactInfoParsedResponse;
  data: {
    programExtensions: ProgramExtension[];
    selfReporterStatuses: BaseEnum[];
    worklistGroups: EmployeeGroup[];
  };
  member: Patient;
  open?: boolean;
  onClose: () => void;
  onSubmit: (
    params: Partial<
      { member: PatientParam } & { contactInfo: MemberContactInfoParams } & {
        programExtensions: ProgramExtensionBulkParams[];
      }
    >,
  ) => Promise<boolean>;
};

const useStyles = makeStyles({
  disabledOption: {
    opacity: 0.38,
    width: '100%',
  },
  footer: {
    marginTop: '-1rem',
  },
});

export const MemberProgramEditModal = ({
  contactInfo,
  data,
  member,
  open,
  onClose,
  onSubmit,
}: Props) => {
  const { actingUser } = Session;
  const styles = useStyles();

  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const [brand, setBrand] = useState<Brand>();
  const [deceasedDate, setDeceasedDate] = useState<Moment>();
  const [isTest, setIsTest] = useState<boolean>(false);
  const [reasonWithdraw, setReasonWithdraw] = useState<ReasonWithdraw>();
  const [startDate, setStartDate] = useState<Moment>();
  const [status, setStatus] = useState<MemberStatus>();

  const [virtualVisitDate, setVirtualVisitDate] = useState<Moment>();
  const [virtualVisitTime, setVirtualVisitTime] = useState<Moment>();
  const [virtualVisitStatus, setVirtualVisitStatus] = useState<
    VirtualVisitStatus
  >();
  const [virtualVisitPlatform, setVirtualVisitPlatform] = useState<
    VirtualVisitPlatform
  >();
  const [virtualVisitHostName, setVirtualVisitHostName] = useState<string>();
  const [
    virtualVisitHostRelationship,
    setVirtualVisitHostRelationship,
  ] = useState<CaregiverRelationship>();
  const [
    virtualVisitHostRelationshipOther,
    setVirtualVisitHostRelationshipOther,
  ] = useState<string>();
  const [
    virtualVisitHostPhoneNumber,
    setVirtualVisitHostPhoneNumber,
  ] = useState<string>();

  const [contactTypes, setContactTypes] = useState<CheckboxItem[]>([]);
  const [contactTypeOther, setContactTypeOther] = useState('');
  const [contactDateTimes, setContactDateTimes] = useState<DatesTimeItem[]>([]);
  const [contactNote, setContactNote] = useState('');

  const [selfReporter, setSelfReporter] = useState<boolean>(false);
  const [hciNotifications, setHCINotifications] = useState<boolean>(false);
  const [authenticationPhone, setAuthenticationPhone] = useState<Phone>();
  const [hciReporterMethod, setHCIReporterMethod] = useState<
    MemberContactMethodType
  >();

  const [programExtensions, setProgramExtensions] = useState<
    MemberProgramExtension[]
  >([]);

  const [worklistGroup, setWorklistGroup] = useState<EmployeeGroup>();
  const [riskLevel, setRiskLevel] = useState<MemberRisk>();

  const isMemberEditable = status !== MemberStatus.INVALID;

  const virtualVisitReadOnly =
    !isMemberEditable ||
    (member?.virtualVisitStatus === VirtualVisitStatus.COMPLETED &&
      !actingUser.canEditMemberVirtualVisitCompleted);

  const validateContactInfo = () =>
    (!getOtherContactType(contactTypes)?.checked || contactTypeOther) &&
    !(contactDateTimes.length > 1
      ? contactDateTimes.find(
          ({ dates, type, startTime, endTime }) =>
            (!dates && !type) ||
            (type === MemberContactTimeType.CUSTOM && !startTime && !endTime),
        )
      : contactDateTimes.find(
          ({ type, startTime, endTime }) =>
            type === MemberContactTimeType.CUSTOM && !startTime && !endTime,
        ));

  const validateSelfReporter = () =>
    !selfReporter ||
    hciReporterMethod !== MemberContactMethodType.MOBILE_APP_CHAT ||
    authenticationPhone;

  const validatePE = ({
    programExtension,
    status,
    statusReason,
    statusReasonOther,
  }: MemberProgramExtension) =>
    programExtension?.id &&
    status &&
    (status?.id !== ProgramExtensionStatus.HOLD || statusReason) &&
    (status?.id !== ProgramExtensionStatus.HOLD ||
      statusReason !== ProgramExtensionStatusReason.OTHER ||
      statusReasonOther);

  const validateProgramExtensions = () => programExtensions.every(validatePE);

  const validate = () =>
    !isMemberEditable
      ? status
      : brand &&
        startDate?.isValid() &&
        status &&
        (status !== MemberStatus.DISENROLLED_WITHDRAW || reasonWithdraw) &&
        (virtualVisitHostRelationship !== CaregiverRelationship.OTHER ||
          virtualVisitHostRelationshipOther) &&
        (!virtualVisitTime || virtualVisitDate) &&
        (!virtualVisitHostPhoneNumber ||
          isValidPhone(virtualVisitHostPhoneNumber)) &&
        validateContactInfo() &&
        validateSelfReporter() &&
        (!actingUser.canEditPE || validateProgramExtensions());

  const buildMemberParams = () => {
    let newVirtualVisitDate: Moment | null = null;
    if (virtualVisitDate) {
      newVirtualVisitDate = virtualVisitDate
        .hours(virtualVisitTime?.hours() || 0)
        .minutes(virtualVisitTime?.minutes() || 0);
    }

    return isMemberEditable
      ? {
          brandId: brand?.value,
          deceasedDate,
          isTest,
          reasonWithdraw,
          reportingStartDate: startDate,
          status: status?.value,

          /** SELF REPORTER */
          hciNotifications,
          hciReporter: selfReporter,
          hciReporterMethod: selfReporter ? hciReporterMethod || null : null,
          authenticationPhone:
            selfReporter &&
            hciReporterMethod === MemberContactMethodType.MOBILE_APP_CHAT
              ? authenticationPhone?.number || null
              : null,

          /** VIRTUAL VISIT */
          virtualVisitDate: newVirtualVisitDate,
          virtualVisitPlatform: virtualVisitPlatform?.value || null,
          virtualVisitStatus: virtualVisitStatus?.value || null,
          virtualVisitHostName,
          virtualVisitHostRelationship:
            virtualVisitHostRelationship?.value || null,
          virtualVisitHostRelationshipOther:
            virtualVisitHostRelationship === CaregiverRelationship.OTHER
              ? virtualVisitHostRelationshipOther
              : null,
          virtualVisitHostPhoneNumber,
          virtualVisitHasTime: virtualVisitDate
            ? !!virtualVisitDate && !!virtualVisitTime
            : undefined,

          riskLevel: riskLevel?.value || null,
          worklistGroupId: worklistGroup?.id,
        }
      : {
          status: status?.value,
        };
  };

  const doSubmit = async () => {
    setSubmitted(true);

    if (!validate()) return;

    setLoadingButton(true);
    try {
      const result = await onSubmit({
        contactInfo: isMemberEditable
          ? buildContactInfoParams(
              contactTypes,
              contactTypeOther,
              contactDateTimes,
              contactNote,
            )
          : undefined,
        programExtensions: isMemberEditable
          ? buildProgramExtensionParams(programExtensions)
          : undefined,
        member: buildMemberParams(),
      });
      if (result) {
        onClose();
      }
    } finally {
      setLoadingButton(false);
    }
  };

  const onChangeContactType = (items: CheckboxItem[]) => {
    const otherOption = getOtherContactType(contactTypes);
    setContactTypes(
      items.map((item) => ({
        ...item,
        disabled:
          otherOption?.checked &&
          item.value !== MemberContactMethodType.OTHER.value,
      })),
    );
  };

  useEffect(() => {
    if (!member) return;

    setDeceasedDate(
      member?.deceasedDate ? moment(member?.deceasedDate) : undefined,
    );
    setAuthenticationPhone(member?.appUser?.authenticationPhone);
    setBrand(member.brand);
    setHCINotifications(!!member?.hciNotifications);
    setHCIReporterMethod(member?.hciReporterMethod);
    setIsTest(member.test);
    setReasonWithdraw(member.reasonWithdraw);
    setSelfReporter(member?.hciReporter);
    setStartDate(moment(member.reportingStartDate));
    setStatus(member.status);
    setVirtualVisitDate(
      member.virtualVisitDate
        ? moment.unix(member.virtualVisitDate)
        : undefined,
    );
    setVirtualVisitHostName(member.virtualVisitHostName);
    setVirtualVisitHostRelationship(member.virtualVisitHostRelationship);
    setVirtualVisitHostRelationshipOther(
      member.virtualVisitHostRelationshipOther,
    );
    setVirtualVisitHostPhoneNumber(
      member.virtualVisitHostPhoneNumber?.toString(),
    );
    setVirtualVisitPlatform(member.virtualVisitPlatform);
    setVirtualVisitStatus(member.virtualVisitStatus);
    setVirtualVisitTime(
      member.virtualVisitDate && member.virtualVisitHasTime
        ? moment.unix(member.virtualVisitDate)
        : undefined,
    );
    setProgramExtensions(
      member.programExtensions?.length
        ? [...member.programExtensions.map(MemberProgramExtension.clone)]
        : [new MemberProgramExtension({})],
    );
    setRiskLevel(member.riskLevel);
    setWorklistGroup(member.worklistGroup);
  }, [member]);

  useEffect(() => {
    if (!contactInfo) {
      setContactDateTimes([]);
      setContactTypes([]);
      setContactTypeOther('');
      setContactNote('');
    } else {
      const {
        contactTypes,
        contactTypesOther,
        contactTimes,
        note,
      } = getDataFromContactInfo(contactInfo);

      setContactTypes(contactTypes);
      setContactTypeOther(contactTypesOther);
      setContactDateTimes(contactTimes);
      setContactNote(note);
    }
  }, [contactInfo]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setLoadingButton(false);
        setSubmitted(false);
      }, 250);
    }
  }, [open]);

  return (
    <CollapsableSidebar
      title={translate(`${I18N_NAMESPACE}.title`)}
      open={!!open}
      onClose={onClose}
      size={650}
    >
      <CollapsableSidebar.Body>
        <div className="grid-wrapper fit">
          <DateTimePicker
            className="grid-span-6"
            disabled={!isMemberEditable || member?.startDateEmrIntegrated}
            disableClereable
            error={submitted && !startDate}
            label={translate(`${I18N_NAMESPACE}.startDate`)}
            maxDate={new Date()}
            onChange={(value?: Date) =>
              setStartDate(value ? moment(value.getTime()) : undefined)
            }
            placeholder={translate('global.chooseADate')}
            required
            value={startDate?.toDate()}
          />

          {actingUser.canEditMemberRiskLevel ? (
            <Select
              className="grid-span-6"
              disabled={!isMemberEditable}
              disableClearable
              error={submitted && !riskLevel}
              label={translate(`${I18N_NAMESPACE}.riskLevel`)}
              onChange={(value?: Selectable) =>
                setRiskLevel(MemberRisk.byKey[value?.value || ''])
              }
              items={[
                UNASSIGNED_SELECTABLE,
                ...Enum.toSelectable(MemberRisk.asArray),
              ]}
              value={
                riskLevel
                  ? Enum.toSelectable([riskLevel])[0]
                  : UNASSIGNED_SELECTABLE
              }
            />
          ) : (
            <div className="grid-span-6" />
          )}

          <Select
            className={`grid-span-${
              status &&
              [
                MemberStatus.DECEASED_ONBOARDED,
                MemberStatus.DECEASED_NON_ONBOARDED,
                MemberStatus.DISENROLLED_WITHDRAW,
              ].includes(status)
                ? 6
                : 12
            }`}
            data-cy="status-modal-select"
            disableClearable
            disabled={!actingUser.canEditMemberStatus}
            error={submitted && !status}
            getItemLabel={Enum.toString}
            getItemDisabled={(item: MemberStatus) =>
              (item === MemberStatus.DECEASED_NON_ONBOARDED &&
                member.wasEverOnboarded) ||
              (item === MemberStatus.DECEASED_ONBOARDED &&
                !member.wasEverOnboarded)
            }
            label={translate(`${I18N_NAMESPACE}.status`)}
            items={
              actingUser.canEditMemberStatusAll
                ? MemberStatus.asArray.filter(
                    (status) => status !== MemberStatus.DECEASED,
                  )
                : [
                    MemberStatus.ACCEPTED,
                    ...(member.status ? [member.status] : []),
                  ]
            }
            onChange={setStatus}
            renderOption={(status: MemberStatus) => {
              const text = status.toString();
              if (
                MemberStatus.INVALID === status &&
                !actingUser.canSetMemberStatusInvalid
              ) {
                return (
                  <Tooltip
                    text={translate(
                      `${I18N_NAMESPACE}.warningNoSetInvalidStatus`,
                    )}
                    enterDelay={50}
                    followCursor
                  >
                    <span
                      className={styles.disabledOption}
                      onClick={(evt) => evt.stopPropagation()}
                    >
                      {' '}
                      {text}
                    </span>
                  </Tooltip>
                );
              }
              return text;
            }}
            required
            value={status}
          />
          {status === MemberStatus.DISENROLLED_WITHDRAW && (
            <Select
              className="grid-span-6"
              error={submitted && !reasonWithdraw}
              disabled={!isMemberEditable}
              getItemLabel={Enum.toString}
              items={ReasonWithdraw.asArray}
              label={translate(`${I18N_NAMESPACE}.reasonWithdraw`)}
              onChange={setReasonWithdraw}
              required
              value={reasonWithdraw}
            />
          )}
          {status &&
            [
              MemberStatus.DECEASED_ONBOARDED,
              MemberStatus.DECEASED_NON_ONBOARDED,
            ].includes(status) && (
              <DateTimePicker
                className="grid-span-6"
                data-cy="encounter-date"
                disableClereable={!isMemberEditable}
                label={translate(`${I18N_NAMESPACE}.deceasedDate`)}
                maxDate={new Date()}
                onChange={(date?: Date) =>
                  setDeceasedDate(date ? moment(date.getTime()) : undefined)
                }
                value={deceasedDate?.toDate()}
              />
            )}

          <div
            className={classNames(
              'grid-span-12 flex gap spaced',
              actingUser.canEditMemberTestFlag && styles.footer,
            )}
          >
            {actingUser.canEditMemberTestFlag ? (
              <CheckboxWithLabel
                checked={isTest}
                disabled={!isMemberEditable}
                data-cy="test-toggle"
                label={translate(`${I18N_NAMESPACE}.testMember`)}
                onChange={() => setIsTest(!isTest)}
              />
            ) : (
              <div />
            )}
            {!!member.statusUpdatedAt && !!member.statusUpdatedBy && (
              <p className="small grey grid-span-12 no-margin">
                {translate('global.lastUpdatedBy', {
                  date: moment
                    .unix(member.statusUpdatedAt)
                    .format(DATE_TIME_FORMAT),
                  editor: member.statusUpdatedBy.fullName,
                })}
              </p>
            )}
          </div>

          {member.status !== status && status === MemberStatus.INVALID && (
            <PanelInfo
              className="grid-span-12"
              type="warning"
              title={translate(`${I18N_NAMESPACE}.warningInvalidStatus`)}
            />
          )}

          <Select
            className="grid-span-6"
            disabled={!isMemberEditable}
            error={submitted && !brand}
            getItemLabel={Enum.toString}
            items={[Brand.VESTA, Brand.CARE_AT_HOME]}
            label={translate(`${I18N_NAMESPACE}.brand`)}
            onChange={setBrand}
            tooltip={translate(`${I18N_NAMESPACE}.warningBrand`)}
            value={brand}
          />

          <Select
            className="grid-span-6"
            disabled={!isMemberEditable}
            disableClearable
            getItemLabel={(g: EmployeeGroup) => g.name}
            label={translate(`${I18N_NAMESPACE}.workListGroup`)}
            onChange={setWorklistGroup}
            items={[UNASSIGNED_GROUP, ...data.worklistGroups]}
            value={worklistGroup || UNASSIGNED_GROUP}
          />

          {actingUser.canEditPE && (
            <MemberProgramEditProgramExtensions
              data={data}
              disabled={!isMemberEditable}
              programExtensions={programExtensions}
              onChange={setProgramExtensions}
              submitted={submitted}
            />
          )}

          <h4 className="grid-span-12">
            {translate(`${I18N_NAMESPACE}.selfReporter`)}
          </h4>

          <Select.Choice
            className="grid-span-6"
            disabled={!isMemberEditable}
            disableClearable
            label={translate(`${I18N_NAMESPACE}.selfReporter`)}
            onChange={(val?: boolean) => setSelfReporter(!!val)}
            value={selfReporter}
          />

          {selfReporter ? (
            <>
              <Select
                className="grid-span-6"
                disabled
                items={data.selfReporterStatuses}
                getItemLabel={({ description }: BaseEnum) => description}
                label={translate(`${I18N_NAMESPACE}.selfReporterStatus`)}
                onChange={() => {}}
                value={
                  member?.hciReporterStatus || data.selfReporterStatuses[0]
                }
              />
              <Select
                className="grid-span-6"
                disabled={!isMemberEditable}
                items={[
                  MemberContactMethodType.MOBILE_APP_CHAT,
                  MemberContactMethodType.TEXT,
                ]}
                footer={
                  <SwitchGroup
                    className="grid-span-6"
                    items={[
                      {
                        label: translate(`${I18N_NAMESPACE}.hciNotifications`),
                        checked: hciNotifications,
                      },
                    ]}
                    onChange={(items) => {
                      setHCINotifications(!!items[0].checked);
                    }}
                  />
                }
                getItemLabel={Enum.toString}
                label={translate(`${I18N_NAMESPACE}.hciReporterMethod`)}
                onChange={setHCIReporterMethod}
                value={hciReporterMethod}
              />
              {hciReporterMethod === MemberContactMethodType.MOBILE_APP_CHAT ? (
                <Select
                  className="grid-span-6"
                  disabled={!isMemberEditable}
                  error={submitted && !authenticationPhone}
                  getItemLabel={({ phone: { number, type } }: UserPhone) =>
                    `${formatPhone(number)} - ${type?.toString()}`
                  }
                  renderOption={(phone: UserPhone) => (
                    <SelectPhoneOption userPhone={phone} />
                  )}
                  items={
                    member?.phones?.filter(
                      (phone) =>
                        phone.phone.type === PhoneType.CELL &&
                        MemberPhone.filterActive(phone),
                    ) || []
                  }
                  label={translate(`${I18N_NAMESPACE}.authenticationPhone`)}
                  onChange={(item?: UserPhone) =>
                    setAuthenticationPhone(item?.phone)
                  }
                  value={member?.phones?.find(
                    ({ phone }) => phone.number === authenticationPhone?.number,
                  )}
                  required
                />
              ) : (
                <div className="grid-span-6" />
              )}
            </>
          ) : (
            <div className="grid-span-6" />
          )}

          <h4 className="grid-span-12">
            {translate(`${I18N_NAMESPACE}.preferredContactInfo`)}
          </h4>

          <CheckboxGroup
            className="grid-span-12"
            columns={6}
            disabled={!isMemberEditable}
            items={contactTypes}
            label={translate(`${I18N_NAMESPACE}.contactInfoMethod`)}
            onChange={onChangeContactType}
          />

          {getOtherContactType(contactTypes)?.checked ? (
            <TextInput
              className="grid-span-12"
              disabled={!isMemberEditable}
              error={
                submitted &&
                !contactTypeOther &&
                translate('global.missingRequiredFieldMin')
              }
              label={translate('global.other')}
              onChange={(value) => setContactTypeOther(value || '')}
              value={contactTypeOther}
            />
          ) : (
            <div className="grid-span-12" />
          )}

          <EditDateTimes
            className="grid-span-12"
            defaultValue={contactDateTimes}
            disabled={!isMemberEditable}
            title={translate(`${I18N_NAMESPACE}.contactInfoDate`)}
            onChange={setContactDateTimes}
            showType
            submitted={submitted}
            size="s"
          />

          <TextArea
            className="grid-span-12"
            disabled={!isMemberEditable}
            label={translate(`${I18N_NAMESPACE}.contactInfoNote`)}
            onChange={(note) => setContactNote(note || '')}
            rows={3}
            value={contactNote}
          />

          <h4 className="grid-span-12">
            {translate(`${I18N_NAMESPACE}.virtualVisitInfo`)}
          </h4>

          <DateTimePicker
            className="grid-span-6"
            disabled={virtualVisitReadOnly}
            label={translate(`${I18N_NAMESPACE}.virtualVisitDate`)}
            onChange={(value?: Date) =>
              setVirtualVisitDate(value ? moment(value.getTime()) : undefined)
            }
            placeholder={translate('global.chooseADate')}
            required={!!virtualVisitTime}
            error={submitted && !!virtualVisitTime && !virtualVisitDate}
            value={virtualVisitDate?.toDate()}
          />
          <DateTimePicker
            className="grid-span-6"
            disabled={virtualVisitReadOnly}
            label={translate(`${I18N_NAMESPACE}.virtualVisitTime`)}
            onChange={(value?: Date) =>
              setVirtualVisitTime(value ? moment(value.getTime()) : undefined)
            }
            placeholder={translate('global.chooseATime')}
            type="time"
            value={virtualVisitTime?.toDate()}
          />
          <Select
            className="grid-span-6"
            disabled={virtualVisitReadOnly}
            getItemLabel={Enum.toString}
            items={VirtualVisitStatus.asArray}
            label={translate(`${I18N_NAMESPACE}.virtualVisitStatus`)}
            onChange={setVirtualVisitStatus}
            placeholder={translate('global.chooseAStatus')}
            value={virtualVisitStatus}
          />
          <Select
            className="grid-span-6"
            disabled={!isMemberEditable}
            getItemLabel={Enum.toString}
            items={VirtualVisitPlatform.asArray}
            label={translate(`${I18N_NAMESPACE}.virtualVisitPlatform`)}
            onChange={setVirtualVisitPlatform}
            placeholder={translate('global.chooseAPlatform')}
            value={virtualVisitPlatform}
          />
          <TextInput
            className="grid-span-6"
            disabled={!isMemberEditable}
            label={translate(`${I18N_NAMESPACE}.virtualVisitHostFullName`)}
            onChange={setVirtualVisitHostName}
            value={virtualVisitHostName}
          />
          <PhoneInput
            className="grid-span-6"
            disabled={!isMemberEditable}
            label={translate(`${I18N_NAMESPACE}.virtualVisitHostPhoneNumber`)}
            onChange={setVirtualVisitHostPhoneNumber}
            submitted={submitted && !!virtualVisitHostPhoneNumber}
            value={virtualVisitHostPhoneNumber}
          />
          <Select
            className={`grid-span-${
              virtualVisitHostRelationship === CaregiverRelationship.OTHER
                ? 6
                : 12
            }`}
            disabled={!isMemberEditable}
            getItemLabel={Enum.toString}
            items={CaregiverRelationship.asArray}
            label={translate(`${I18N_NAMESPACE}.virtualVisitHostRelationship`)}
            onChange={setVirtualVisitHostRelationship}
            value={virtualVisitHostRelationship}
          />
          {virtualVisitHostRelationship === CaregiverRelationship.OTHER && (
            <TextInput
              className="grid-span-6"
              disabled={!isMemberEditable}
              error={submitted && !virtualVisitHostRelationship}
              label={translate('global.other')}
              onChange={setVirtualVisitHostRelationshipOther}
              required
              value={virtualVisitHostRelationshipOther}
            />
          )}
        </div>
      </CollapsableSidebar.Body>
      <CollapsableSidebar.Buttons>
        <Button color="tertiary" onClick={onClose}>
          {translate('global.close')}
        </Button>
        <Button color="secondary" loading={loadingButton} onClick={doSubmit}>
          {translate('global.save')}
        </Button>
      </CollapsableSidebar.Buttons>
    </CollapsableSidebar>
  );
};
